import { loadTemplate } from "utils/load_template"
import AttendanceVerificationManager from 'cle/attendance_verification_manager'
import CLECourseChannel from 'channels/cle/course_channel'
import VideoDataStore from 'models/video_data_store'

export default class OnDemandAttendanceVerification {
  constructor(config, modal, telemeter, onFatalError, errorReporter) {
    this.SECONDCHANGE_THRESHOLD_IN_SECS = 1.0
    this.config = config
    this.dataStore = VideoDataStore.getStore(config.videoID)
    this.avm = new AttendanceVerificationManager(config, modal, this.log, onFatalError, errorReporter)
    this.checkpoints = this.avm.checkpoints
    this.numSecondsUntilEndToConsiderVideoCompleted = config.videoEndToleranceSeconds
    this.isChimeEnabled = false
    this.hasInitializedAttendance = config.attendanceAlreadyCompleted
    this.lastValidActiveSecond = 0
    this.telemeter = telemeter
    this.onFatalError = onFatalError
  }

  //
  // Pass in audioContext and modal here to make our JS tests easier to write
  run(video) {
    const that = this
    this.video = video

    this.lastValidActiveSecond = this.dataStore.getMaxWatchedSecond()

    const duration = video.duration()

    this.bindVideoEventWithErrorHandling("secondchange", (second) => this.onSecondChange(video, second))

    this.bindVideoEventWithErrorHandling("end", () => {
      this.log("Video 'end' event fired")
      this.completeAttendance()
    })

    this.bindVideoEventWithErrorHandling("betweentimes", duration - this.numSecondsUntilEndToConsiderVideoCompleted, duration, () => this.completeAttendance() )

    const courseChannel = new CLECourseChannel(this, video, this.pauseSimultaneousVideo)
    courseChannel.run()

    this.bindVideoEventWithErrorHandling("play", () => {
      this.log("Video 'play' event fired")
      if(!this.hasInitializedAttendance) {
        this.initializeServerAttendance().then(() => { this.hasInitializedAttendance = true }).catch(this.onFatalError)
      }
      courseChannel.sendVideoPlayEvent()
    })

    this.checkIfAttendanceShouldBeCompleted(duration)
  }

  checkIfAttendanceShouldBeCompleted(videoDuration){
    if(this.avm.uncheckedCheckpoints().length === 0 &&
      (videoDuration - this.dataStore.getMaxWatchedSecond() <= this.numSecondsUntilEndToConsiderVideoCompleted) &&
      !this.avm.attendanceCompleted) {
      // the attendance checkpoints are checked and the user has watched until the end.
      // Perhaps there was an error when we tried to complete the attendance before, so just try again now.
      this.completeAttendance()
    }
  }

  completeAttendance() {
    this.avm.completeAttendance(() => {
      this.log("Attendance completed on server. Enabling 'Claim certificate' button.")
      const $cleCourseCtaButtons = document.querySelectorAll("[data-js-cle-course-certificate-button]")

      $cleCourseCtaButtons.forEach((btn) => {
        btn.classList.remove("u-disable-pointer-events")
        btn.disabled = false
      })
    })
  }

  onSecondChange(video, second) {
    // Go back to maxWatchedSecond if the user fast forwarded ahead of maxWatchedSecond more than 1s (doable through browser extensions).
    // This is skipped if the playbar is active (only for admins).
    if (this.isFastForwardedAboveMaxWatchedSecond(second)) {
      this.video.time(this.dataStore.getMaxWatchedSecond())
      return false
    }

    this.trackWatchedSecond(second)
    this.rewindIfNecessary(second)

    // Reset playback speed to avoid video accelerator plugins
    if(video.playbackRate() !== 1) {
      this.log(`Video playbackRate was not equal to 1! playbackRate: ${video.playbackRate()}`)
    }
    video.playbackRate(1)

    this.dataStore.setLimitForward(true)

    if (this.isNewCheckpoint(second)) {
      this.setupAndDisplayCheckpointPrompt(second)
    }
  }

  isFastForwardedAboveMaxWatchedSecond(currentTime) {
    if (this.video.controls.playbar) return false
    if (this.avm.checkpoints.some(checkpoint => checkpoint.shown)) return false

    return ((currentTime - this.dataStore.getMaxWatchedSecond()) > this.SECONDCHANGE_THRESHOLD_IN_SECS)
  }

  trackWatchedSecond(currentSecond){
    const missedCheckpointPosition = this.avm.uncheckedCheckpoints().map((cp) => cp.position).sort((cp)=> cp.position)[0] || this.video.duration()
    if(currentSecond < missedCheckpointPosition){
      // only track the lastWatchedSecond/maxWatchedSecond if it's not past a missing checkpoint.
      this.setMaxWatchedSecond(Math.max(this.dataStore.getMaxWatchedSecond() || 0, currentSecond))
      this.dataStore.setLastWatchedSecond(currentSecond)
    }

    // We can't just use the data store's last active second without checking that it's valid
    // in the context of a CLE course.
    // We check if the active second was _before_ the last valid maxWatchedSecond,
    // which makes sure we don't track user activity after a missed checkpoint.
    if(this.dataStore.getLastActiveSecond() <= this.dataStore.getMaxWatchedSecond()) {
      this.lastValidActiveSecond = this.dataStore.getLastActiveSecond()
      this.lastValidActiveSecond = Math.max(this.lastCheckedCheckpoint(), this.lastValidActiveSecond)
    }
  }

  rewindIfNecessary(currentSecond){
    const missedCheckpointPosition = this.avm.uncheckedCheckpoints().map((cp) => cp.position).sort((cp)=> cp.position)[0] || this.video.duration()
    const endOfAttendancePromptCountDown = missedCheckpointPosition + this.avm.attendancePopupCountDownSeconds
    if(currentSecond > endOfAttendancePromptCountDown) {
      // The user can watch past a checkpoint without anything happening, but they can't
      // watch past the end of the checkpoint's countdown.
      // If they somehow get there, we need to rewind the video to the last valid 'active' time.
      this.goBackToLastActiveSecond()
    }
  }

  setupAndDisplayCheckpointPrompt(second){
    this.log(`Setting up and displaying checkpoint prompt for ${second} seconds`)
    const video = this.video

    // To do HTML/CSS work on the popups, comment out the `if` below and
    // uncomment the appropriate function call here:
    // this.displayCheckpointPrompt(video, node, this.checkpoints.first)
    // this.displayExpiredPopup(video, second)
    // this.displaySimultaneousPopup(video, this.config.videoName)

    this.displayCheckpointPrompt(video, second)
  }

  setMaxWatchedSecond(second) {
    this.dataStore.setMaxWatchedSecond(second)
  }

  isNewCheckpoint(second) {
    const checkpoint = this.avm.findCheckPoint(second)
    if(!checkpoint) { return false }
    return checkpoint.position === second && !checkpoint.checked_at && !checkpoint.shown
  }

  lastCheckedCheckpoint() {
    return this.avm.checkpoints.filter(cp => cp.checked_at).map(cp => cp.position).sort(cp => -cp.position)[0] || 0
  }

  displayCheckpointPrompt(video, second) {
    this.avm.displayCheckpointPrompt((node) => {
      video.grid.center.appendChild(node)
      video.fit()
      this.startPromptCountDown(node, video, second)
    })
  }

  pauseSimultaneousVideo(title) {
    if (this.video.state() === "playing") {
      this.log(`Prevented simultaneous videos from playing (${title}).`)
      this.displaySimultaneousPopup(this.video, title)
    }
  }

  displaySimultaneousPopup(video, title) {
    video.pause()

    const node = document.createElement("div")
    node.innerHTML = loadTemplate(this.config.simultaneousVideoAlertTemplate).interpolateObject({ title: title })

    video.grid.center.appendChild(node)
    video.fit()

    const buttonEl = node.querySelector("button")

    buttonEl.addEventListener("click", (_) => {
      // Close the popup
      video.grid.center.removeChild(node)
    })

    return document.querySelector(this.config.simultaneousVideoAlertContainerSelector)
  }

  goBackToLastActiveSecond() {
    // We won't let you fast forward beyond the previous active action. You will
    // need to watch it all again.
    this.log(`Going back to last active second of ${this.lastValidActiveSecond}`)
    this.setMaxWatchedSecond(this.lastValidActiveSecond)

    this.video.time(this.lastValidActiveSecond)
  }

  startPromptCountDown(node, video, second) {
    this.avm.startPromptCountDown(
      node,
      second,
      {
        onClose: (node) => {
          video.grid.center.removeChild(node)
        },
        displayExpiredPopup: (node) => {
          video.grid.center.appendChild(node)
          video.fit()
          video.pause()
          // this.resetToLastCheckedCheckpoint() // We need to check whether we need to do this.
        },
        clickExpiredPopup: (node) => {
          this.goBackToLastActiveSecond()
          // Close the popup
          video.grid.center.removeChild(node)
          video.play()
        }
      }
    )
  }

  // For testing purposes only. This needs to be refactored ASAP.
  markCheckpoint(checkpoint, time) {
    this.avm.markCheckpoint(checkpoint, time)
  }

  // For testing purposes only. This needs to be refactored ASAP.
  uncheckedCheckpoints() {
    return this.avm.uncheckedCheckpoints()
  }

  resetToLastCheckedCheckpoint() {
    this.dataStore.setLastActiveSecond(this.lastCheckedCheckpoint())
    this.lastValidActiveSecond = this.lastCheckedCheckpoint()
  }

  // We want to create an attendance record in the database as soon as a user
  // starts watching a course.
  initializeServerAttendance(){
    return this.avm.submitCheckpointsToServer()
  }

  log(message) {
    if(!this.telemeter) { return }
    // we don't want logging to ever _cause_ errors, so try/catch as a fail safe
    try {
      this.telemeter({
        message: message,
        videoTime: this.video.time()
      }, "info")
    } catch(e) {
      console.log(e)
    }
  }

  ////
  // Wrap the wistia JS video.bind() call so that we can
  // listen for errors and show the error modal.
  // Because we re-throw the error, it should still be caught by Rollbar.
  // NOTE: This does NOT do anything if a promise inside of the listener
  // function is rejected. You must manually use onFatalError in a `.catch(...)` call.
  bindVideoEventWithErrorHandling(eventName, ...rest) {
    const listener = rest.pop()
    const otherArgs = rest
    const wrappedListener = (...args) => {
      try {
        listener(...args)
      } catch (e) {
        this.onFatalError(e)
        throw e
      }
    }
    const newArgs = [...otherArgs, wrappedListener]
    this.video.bind(eventName, ...newArgs)
  }
}
